/* Pure decision helpers for the dual-transport WebRTC signalling path.
 * Kept free of React/WebRTC so they can be unit tested in isolation. */

type SignalPayload = {
  type?: unknown
  Type?: unknown
  sdp?: {
    type?: unknown
    candidate?: unknown
  }
}

const getNormalizedType = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') return undefined
  const { type, Type } = payload as SignalPayload
  const value = type ?? Type
  if (value == null || value === '') return undefined
  return String(value).toLowerCase()
}

const getSdp = (payload: unknown): SignalPayload['sdp'] | undefined => {
  if (!payload || typeof payload !== 'object') return undefined
  const sdp = (payload as SignalPayload).sdp
  return sdp && typeof sdp === 'object' ? sdp : undefined
}

/**
 * Centrifugo echoes a publication back to its own subscriber. That path has
 * no `payload.from`, so compare the publication's `info.user` with the `sub`
 * of the JWT passed to `centrifuge.setToken()` instead.
 *
 * Fail open: if either side is missing we cannot prove ownership, and a
 * signal must never be dropped on uncertain grounds.
 */
export const isOwnCentrifugoMessage = (
  infoUser: string | undefined,
  jwtSub: string | undefined
): boolean => {
  if (!infoUser || !jwtSub) return false
  return infoUser === jwtSub
}

/**
 * Only a real peer SDP signal may pin the active signalling source.
 * Status events (ready/accepted/completed/...), unknown types and broken
 * payloads pass the gate but must never lock the source.
 */
export const shouldPinSignalSource = (payload: unknown): boolean => {
  const type = getNormalizedType(payload)
  if (!type) return false

  const sdp = getSdp(payload)
  if (!sdp) return false

  if (type === 'offer' || type === 'answer') {
    return Boolean(sdp.type)
  }
  if (type === 'candidate') {
    return Boolean(sdp.candidate)
  }
  return false
}
